import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { copyTextToClipboard } from '@/features/guide-identity/utils/identityUrl';
import {
  buildCaption,
  canShareFiles,
  facebookShareUrl,
  nativeShare,
  openShareWindow,
  whatsappShareUrl,
  type CaptionChannel,
} from '../utils/share';
import { downloadPromo, renderPromoFile } from '../utils/promoExport';
import { PROMO_SIZES, PromoPostCanvas, type PromoFormat } from './PromoPostCanvas';
import { useRewardsToast } from './RewardsToast';

const SAGE = '#1A2E1A';
const GOLD = '#C8A96E';

/**
 * Width of a box, kept current as it resizes. A callback ref, because the
 * drawer mounts its content only when it opens.
 */
function useElementWidth() {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return { ref: setNode, width };
}

function ShareChannel({
  label,
  icon,
  background,
  onClick,
  busy,
}: {
  label: string;
  icon: ReactNode;
  background: string;
  onClick: () => void;
  busy?: boolean;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={busy}
      aria-label={label}
      sx={{
        flexDirection: 'column',
        gap: 0.75,
        py: 1,
        borderRadius: 3,
        '&:hover .share-icon, &.Mui-focusVisible .share-icon': { transform: 'translateY(-2px)', boxShadow: 'var(--shadow-md)' },
        '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      <Box
        className="share-icon"
        sx={{
          width: 54,
          height: 54,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          color: '#FFFFFF',
          background,
          opacity: busy ? 0.6 : 1,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '& .MuiSvgIcon-root': { fontSize: 26 },
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>
    </ButtonBase>
  );
}

interface ShareInviteDrawerProps {
  open: boolean;
  onClose: () => void;
  referralLink: string;
}

/**
 * Bottom sheet for inviting guides: link, branded promo post, one-tap share
 * channels and ready-made captions. Nothing is ever posted automatically —
 * every channel is opened by the guide's own tap.
 */
export function ShareInviteDrawer({ open, onClose, referralLink }: ShareInviteDrawerProps) {
  const { t } = useTranslation('rewards');
  const notify = useRewardsToast();
  const [format, setFormat] = useState<PromoFormat>('portrait');
  const [captionTab, setCaptionTab] = useState<CaptionChannel>('whatsapp');
  const [busy, setBusy] = useState(false);
  const [showInstagramHelp, setShowInstagramHelp] = useState(false);
  const exportRefs = useRef<Partial<Record<PromoFormat, HTMLDivElement | null>>>({});
  const preview = useElementWidth();

  useEffect(() => {
    if (!open) setShowInstagramHelp(false);
  }, [open]);

  const copy = async (text: string, success: string) => {
    const ok = await copyTextToClipboard(text);
    notify(ok ? success : t('toast.copyFailed'), ok ? 'success' : 'error');
    return ok;
  };

  const exportNode = (which: PromoFormat) => exportRefs.current[which] ?? null;

  const download = async (which: PromoFormat = format) => {
    const node = exportNode(which);
    if (!node) return;
    setBusy(true);
    try {
      await downloadPromo(node, which);
      notify(t('toast.imageSaved'));
    } catch {
      notify(t('toast.imageFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const shareInstagram = async () => {
    const node = exportNode('portrait');
    if (!node) return;
    setBusy(true);
    try {
      const caption = buildCaption('instagram', referralLink);
      // Instagram drops shared text, so the caption goes to the clipboard first.
      await copyTextToClipboard(caption);
      const file = await renderPromoFile(node, 'portrait');
      if (canShareFiles(file)) {
        const outcome = await nativeShare({ files: [file], text: caption });
        if (outcome === 'shared') notify(t('toast.shared'));
        if (outcome !== 'failed') return;
      }
      setFormat('portrait');
      setCaptionTab('instagram');
      setShowInstagramHelp(true);
    } catch {
      notify(t('toast.imageFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const shareMore = async () => {
    const outcome = await nativeShare({
      title: 'Join me on Trplin',
      text: buildCaption('whatsapp', referralLink),
      url: referralLink,
    });
    if (outcome === 'shared') notify(t('toast.shared'));
    if (outcome === 'unsupported' || outcome === 'failed') {
      await copy(referralLink, t('toast.linkCopied'));
    }
  };

  const { width: fullW, height: fullH } = PROMO_SIZES[format];
  const maxPreview = format === 'landscape' ? 520 : format === 'square' ? 340 : 300;
  const previewW = Math.min(preview.width || maxPreview, maxPreview);
  const scale = previewW / fullW;

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          mx: 'auto',
          width: '100%',
          maxWidth: 720,
          maxHeight: '92dvh',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          pb: 'env(safe-area-inset-bottom)',
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15, 26, 15, 0.55)', backdropFilter: 'blur(2px)' } } }}
    >
      <Box role="dialog" aria-labelledby="share-drawer-title" sx={{ overflowY: 'auto', overscrollBehavior: 'contain' }}>
        <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', pt: 1.25, px: { xs: 2.5, sm: 3.5 }, pb: 1.5 }}>
          <Box aria-hidden sx={{ width: 44, height: 5, borderRadius: 3, bgcolor: 'grey.300', mx: 'auto', mb: 1.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography id="share-drawer-title" variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
                {t('share.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {t('share.subtitle')}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label={t('share.close')} edge="end">
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pb: 3.5, display: 'grid', gap: 3 }}>
          {/* Link card */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              gap: 1.5,
              p: 1.5,
              borderRadius: 3,
              bgcolor: 'rgba(200,169,110,0.10)',
              border: '1px solid rgba(200,169,110,0.35)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <LinkRoundedIcon sx={{ color: GOLD, flexShrink: 0 }} />
              <Typography
                title={referralLink}
                sx={{ fontWeight: 600, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {referralLink}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<ContentCopyRoundedIcon />}
              onClick={() => copy(referralLink, t('toast.linkCopied'))}
              sx={{ borderRadius: 999, fontWeight: 700, bgcolor: SAGE, flexShrink: 0, '&:hover': { bgcolor: '#243D24' } }}
            >
              {t('share.copyLink')}
            </Button>
          </Box>

          {/* Share channels */}
          <Box>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.12em' }}>
              {t('share.shareVia')}
            </Typography>
            <Box sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 0.5 }}>
              <ShareChannel
                label={t('share.channels.whatsapp')}
                icon={<WhatsAppIcon />}
                background="#25D366"
                onClick={() => openShareWindow(whatsappShareUrl(referralLink))}
              />
              <ShareChannel
                label={t('share.channels.facebook')}
                icon={<FacebookIcon />}
                background="#1877F2"
                onClick={() => openShareWindow(facebookShareUrl(referralLink))}
              />
              <ShareChannel
                label={t('share.channels.instagram')}
                icon={<InstagramIcon />}
                background="radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)"
                onClick={shareInstagram}
                busy={busy}
              />
              <ShareChannel
                label={t('share.channels.copy')}
                icon={<ContentCopyRoundedIcon />}
                background={SAGE}
                onClick={() => copy(referralLink, t('toast.linkCopied'))}
              />
              <ShareChannel
                label={t('share.channels.more')}
                icon={<IosShareRoundedIcon />}
                background={GOLD}
                onClick={shareMore}
              />
            </Box>
          </Box>

          {/* Instagram fallback */}
          <Collapse in={showInstagramHelp} unmountOnExit>
            <Box sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
              <Typography sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                <InstagramIcon fontSize="small" /> {t('share.instagramTitle')}
              </Typography>
              <Box component="ol" sx={{ m: 0, mt: 1, pl: 2.5, display: 'grid', gap: 0.5, color: 'text.secondary', fontSize: '0.875rem' }}>
                <li>{t('share.instagramSteps.one')}</li>
                <li>{t('share.instagramSteps.two')}</li>
                <li>{t('share.instagramSteps.three')}</li>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('share.instagramNote')}
              </Typography>
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" size="small" startIcon={<DownloadRoundedIcon />} onClick={() => download('portrait')} disabled={busy} sx={{ borderRadius: 999 }}>
                  {busy ? t('share.preparing') : t('share.download')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyRoundedIcon />}
                  onClick={() => copy(buildCaption('instagram', referralLink), t('toast.captionCopied'))}
                  sx={{ borderRadius: 999 }}
                >
                  {t('share.copyCaption')}
                </Button>
              </Box>
            </Box>
          </Collapse>

          {/* Promo preview */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              <Typography sx={{ fontWeight: 800 }}>{t('share.previewTitle')}</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={format}
                onChange={(_e, value: PromoFormat | null) => value && setFormat(value)}
                aria-label={t('share.previewTitle')}
              >
                {(['portrait', 'square', 'landscape'] as const).map((value) => (
                  <ToggleButton key={value} value={value} sx={{ px: 1.5, textTransform: 'none', fontWeight: 600 }}>
                    {t(`share.formats.${value}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box ref={preview.ref} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: previewW,
                  height: (previewW * fullH) / fullW,
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: 'var(--shadow-lg)',
                  flexShrink: 0,
                }}
              >
                <Box sx={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: fullW, height: fullH }}>
                  <PromoPostCanvas format={format} />
                </Box>
              </Box>
              <Button variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={() => download()} disabled={busy} sx={{ borderRadius: 999, fontWeight: 700 }}>
                {busy ? t('share.preparing') : t('share.download')}
              </Button>
            </Box>
          </Box>

          {/* Captions */}
          <Box>
            <Typography sx={{ fontWeight: 800 }}>{t('share.captionsTitle')}</Typography>
            <Tabs
              value={captionTab}
              onChange={(_e, value: CaptionChannel) => setCaptionTab(value)}
              variant="fullWidth"
              sx={{ mt: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}
            >
              <Tab value="whatsapp" label={t('share.captionTabs.whatsapp')} />
              <Tab value="facebook" label={t('share.captionTabs.facebook')} />
              <Tab value="instagram" label={t('share.captionTabs.instagram')} />
            </Tabs>
            <Box
              component="pre"
              tabIndex={0}
              sx={{
                m: 0,
                mt: 1.5,
                p: 2,
                maxHeight: 220,
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit',
                fontSize: '0.85rem',
                lineHeight: 1.6,
                borderRadius: 3,
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {buildCaption(captionTab, referralLink)}
            </Box>
            <Button
              sx={{ mt: 1, borderRadius: 999, fontWeight: 700 }}
              startIcon={<ContentCopyRoundedIcon />}
              onClick={() => copy(buildCaption(captionTab, referralLink), t('toast.captionCopied'))}
            >
              {t('share.copyCaption')}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            {t('share.privacyNote')}
          </Typography>
        </Box>
      </Box>

      {/* Full-size render targets for export; never visible. */}
      {open && (
        <Box aria-hidden sx={{ position: 'fixed', left: -20000, top: 0, pointerEvents: 'none' }}>
          {(['portrait', 'square', 'landscape'] as const).map((value) => (
            <PromoPostCanvas
              key={value}
              format={value}
              ref={(node) => {
                exportRefs.current[value] = node;
              }}
            />
          ))}
        </Box>
      )}
    </SwipeableDrawer>
  );
}
